// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Link.tsx
 * @input Uses React, AnchorHTMLAttributes, ReactNode
 * @output Exports Link component, LinkProps
 * @position Core implementation; consumed by index.ts, tested by Link.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Link/Link.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Link/Link.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Link/index.ts (exports if types change)
 * - /apps/storybook/stories/Link.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Link/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, durationVars, easeVars, spacingVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { Icon } from "../Icon/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { Text } from "../Text/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useLinkComponent } from "./useLinkComponent.js";
import { mergeProps } from "../utils/index.js";
import { computeTargetAndRel } from "./computeTargetAndRel.js";
import { useInteractiveRole } from "../hooks/useInteractiveRole.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

/**
 * Base link styles
 */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  base: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1lsbc85",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1qlqyl8",
    kLWn49: "x15bjb6t",
    k63SB2: "x1pd3egz",
    kybGjl: "x1hl2dhg x4ohgrr",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x1mpt4pi",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  },
  buttonReset: {
    kWkggS: "xjbqb8w",
    ksu8eU: "xng3xce",
    kmVPX3: "x1717udv",
    kfzvcC: "x67bb7w",
    kVAEAm: "x1n2onr6",
    $$css: true
  },
  hasUnderline: {
    kybGjl: "x1bvjpef",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    kfzvcC: "x47corl",
    $$css: true
  },
  standalone: {
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  }
};

/**
 * Link color styles — applied to the <a> element so the underline
 * and icon colors match the text color set by Text.
 */
const linkColorStyles = {
  primary: {
    kMwMTN: "x1tgivj0 x4z56r7",
    $$css: true
  },
  secondary: {
    kMwMTN: "xv1l7n4 x7ruf5d",
    $$css: true
  },
  disabled: {
    kMwMTN: "xnbbluu",
    $$css: true
  },
  placeholder: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  accent: {
    kMwMTN: "xjse4m1 x17qfo7y",
    $$css: true
  },
  inherit: {
    kMwMTN: "x1heor9g",
    $$css: true
  }
};
/**
 * Click handler for disabled links. The disabled anchor renders without an
 * href, so there is no navigation to block in practice; preventDefault is a
 * defensive guard against synthetic/programmatic clicks.
 */
function preventDefaultClick(event) {
  event.preventDefault();
}

/**
 * A styled anchor link component.
 *
 * Uses Text internally for typography styling.
 * Wrap your app in <Theme> to apply a theme.
 *
 * @example
 * ```
 * <Link href="/docs">Documentation</Link>
 * <Link href="https://github.com" isExternalLink>GitHub</Link>
 * <Link href="/settings" color="secondary">Settings</Link>
 * <Link href="/privacy" hasUnderline>Privacy Policy</Link>
 * <Link label="Close dialog" href="/home"><Icon icon="x" /></Link>
 * <Text type="large">
 *   Read our <Link href="/terms" type="inherit">terms</Link> first.
 * </Text>
 * ```
 */
export function Link({
  as,
  label,
  href,
  hasUnderline = false,
  isDisabled = false,
  isExternalLink = false,
  newTabLabel: newTabLabelFromProps,
  target: targetFromProps,
  onClick,
  tooltip,
  isStandalone = false,
  type = 'body',
  size,
  weight,
  color = 'accent',
  display = 'inline',
  maxLines = 0,
  children,
  rel: relFromProps,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const t = useTranslator();
  const newTabLabel = newTabLabelFromProps ?? t('@astryx.link.newTab');
  const LinkComponent = useLinkComponent(as);
  const role = useInteractiveRole({
    href,
    onClick,
    isDisabled
  });
  // Determine target and rel based on isExternalLink
  const {
    target,
    rel
  } = computeTargetAndRel(isExternalLink ? '_blank' : targetFromProps, relFromProps);

  // When role resolves to 'button' (no href, or context-provided),
  // render as a <button> with link styling for semantic correctness.
  const renderAsButton = role === 'button' || role === 'inert' && href == null;
  const sharedContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      type: type,
      size: size,
      weight: weight,
      color: color,
      display: display,
      maxLines: maxLines,
      children: children
    }), isExternalLink && !renderAsButton && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Icon, {
        icon: "externalLink",
        size: "xsm",
        color: "inherit"
      }), /*#__PURE__*/_jsx(VisuallyHidden, {
        children: newTabLabel
      })]
    })]
  });
  let linkElement;
  if (renderAsButton) {
    linkElement = /*#__PURE__*/_jsx("button", {
      ref: ref,
      type: "button",
      onClick: onClick,
      "aria-label": label || undefined,
      "aria-disabled": isDisabled || undefined,
      tabIndex: isDisabled ? -1 : undefined,
      disabled: isDisabled,
      ...mergeProps(themeProps('link', {
        color
      }), stylex.props(styles.base, styles.buttonReset, linkColorStyles[color], hasUnderline && styles.hasUnderline, isStandalone && styles.standalone, isDisabled && styles.disabled, xstyle), className, style),
      ...props,
      children: sharedContent
    });
  } else if (isDisabled) {
    // A disabled link renders as a plain <a> with no href: an href-less
    // anchor is not focusable and exposes no link affordance, so programmatic
    // focus + Enter, AT activation commands, and middle-click cannot navigate
    // or fire the consumer onClick. The router LinkComponent is deliberately
    // skipped — a disabled link performs no navigation, and custom router
    // links may require a live href. target/rel are omitted with the href.
    linkElement = /*#__PURE__*/_jsx("a", {
      ref: ref,
      onClick: preventDefaultClick,
      "aria-label": label || undefined,
      "aria-disabled": true,
      tabIndex: -1,
      ...mergeProps(themeProps('link', {
        color
      }), stylex.props(styles.base, linkColorStyles[color], hasUnderline && styles.hasUnderline, isStandalone && styles.standalone, styles.disabled, xstyle), className, style),
      ...props,
      children: sharedContent
    });
  } else {
    linkElement = /*#__PURE__*/_jsx(LinkComponent, {
      ref: ref,
      href: href,
      target: target,
      rel: rel,
      onClick: onClick,
      "aria-label": label || undefined,
      "aria-disabled": isDisabled || undefined,
      tabIndex: isDisabled ? -1 : undefined,
      ...mergeProps(themeProps('link', {
        color
      }), stylex.props(styles.base, linkColorStyles[color], hasUnderline && styles.hasUnderline, isStandalone && styles.standalone, isDisabled && styles.disabled, xstyle), className, style),
      ...props,
      children: sharedContent
    });
  }

  // Wrap with tooltip if provided
  if (tooltip) {
    return /*#__PURE__*/_jsx(Tooltip, {
      content: tooltip,
      placement: "above",
      children: linkElement
    });
  }
  return linkElement;
}
Link.displayName = 'Link';